import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar, Camera, AlertTriangle, Search, ChevronRight, ChevronDown, UserRound, Briefcase, Filter, X } from "lucide-react";
import { AddressLink } from "../components/AddressLink";

const statusColor = { active: "bg-primary text-primary-foreground", scheduled: "bg-accent text-accent-foreground", completed: "bg-[hsl(var(--success))] text-white" };

// Date helpers (week = Sunday..Saturday, matching US jobsite convention)
const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const thisWeekRange = () => {
  const now = new Date();
  const ws = new Date(now); ws.setDate(now.getDate() - now.getDay());
  const we = new Date(ws); we.setDate(ws.getDate() + 6);
  return { from: fmtD(ws), to: fmtD(we) };
};

// Grouped date separator: ———— Tue · Aug 20th ———— (Past/Upcoming views)
const ordinalEn = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

function DateSeparator({ dateStr, lang, t }) {
  const d = new Date(`${dateStr}T12:00:00`);
  const now = new Date();
  const tmr = new Date(now); tmr.setDate(now.getDate() + 1);
  const yst = new Date(now); yst.setDate(now.getDate() - 1);
  let rel = null;
  if (dateStr === fmtD(now)) rel = t("today");
  else if (dateStr === fmtD(tmr)) rel = t("tomorrow");
  else if (dateStr === fmtD(yst)) rel = t("yesterday");
  const locale = lang === "pt" ? "pt-BR" : "en-US";
  const wd = d.toLocaleDateString(locale, { weekday: "short" }).replace(".", "");
  const mo = d.toLocaleDateString(locale, { month: "short" }).replace(".", "");
  const yr = d.getFullYear() !== now.getFullYear() ? `, ${d.getFullYear()}` : "";
  const label = lang === "pt" ? `${wd} · ${d.getDate()} ${mo}${yr}` : `${wd} · ${mo} ${ordinalEn(d.getDate())}${yr}`;
  return (
    <div className="flex items-center gap-3 mb-3" data-testid={`jobs-date-separator-${dateStr}`}>
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
        {rel && <span className="text-primary">{rel} · </span>}{label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// Renders jobs either as a flat grid or grouped by day with date separators.
// The incoming list is already sorted (date, then PM1, PM2, ...), so groups
// come out in the right order automatically.
function GroupedJobs({ jobs, grouped, gridClass, lang, t, renderCard }) {
  if (!grouped) return <div className={gridClass}>{jobs.map(renderCard)}</div>;
  const groups = [];
  const idx = {};
  for (const j of jobs) {
    const key = (j.scheduled_date || "").slice(0, 10) || "?";
    if (idx[key] === undefined) { idx[key] = groups.length; groups.push({ key, jobs: [] }); }
    groups[idx[key]].jobs.push(j);
  }
  return groups.map((g) => (
    <div key={g.key} className="mb-5 last:mb-0" data-testid={`jobs-day-group-${g.key}`}>
      <DateSeparator dateStr={g.key} lang={lang} t={t} />
      <div className={gridClass}>{g.jobs.map(renderCard)}</div>
    </div>
  ));
}

function JobTypeBadge({ type, t, small }) {
  if (!type) return null;
  const service = type === "service";
  return (
    <Badge
      data-testid="job-type-badge"
      variant={service ? "default" : "outline"}
      className={`${small ? "text-[9px] px-1.5 py-0" : "text-[10px] px-2 py-0.5"} font-semibold uppercase tracking-wide shrink-0 ${service ? "bg-accent text-accent-foreground hover:bg-accent" : "border-primary/40 text-primary"}`}
    >
      {service ? t("job_type_service") : t("job_type_install")}
    </Badge>
  );
}

function RemovedBadge({ job, t, small }) {
  if (!job.removed_from_source) return null;
  return (
    <Badge
      data-testid="job-removed-badge"
      variant="outline"
      className={`${small ? "text-[9px] px-1.5 py-0" : "text-[10px] px-2 py-0.5"} font-semibold uppercase tracking-wide shrink-0 border-destructive/40 text-destructive bg-destructive/10`}
    >
      {t("removed_from_matrix")}
    </Badge>
  );
}

function DayProgressBadge({ job, t, small }) {
  if (!job.total_days || job.total_days < 2) return null;
  // Active multi-day job: running counter "Day 1 of 2". Otherwise: total "2 days".
  const label = job.day_number
    ? t("day_of_days").replace("{d}", job.day_number).replace("{n}", job.total_days)
    : t("days_count").replace("{n}", job.total_days);
  return (
    <Badge
      data-testid="job-day-badge"
      variant="secondary"
      className={`${small ? "text-[9px] px-1.5 py-0" : "text-[10px] px-2 py-0.5"} font-semibold shrink-0`}
    >
      {label}
    </Badge>
  );
}

function JobCard({ job, lang, t, showSupervisor, compact, onOpen }) {
  if (compact) {
    // Smaller card for "other supervisors" jobs — informative but visually secondary
    return (
      <Card
        data-testid={`job-card-${job.job_number}`}
        onClick={onOpen}
        className="p-3 cursor-pointer hover:bg-muted/40 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
              {job.crew?.code && (
                <Badge data-testid="job-card-crew-badge" className="bg-primary/80 text-primary-foreground font-mono-plex text-[13px] leading-none tracking-wider px-2 py-1 rounded-md">
                  {job.crew.code}
                </Badge>
              )}
              {job.supervisor?.name && (
                <Badge variant="outline" data-testid="job-card-supervisor-badge" className="text-[10px] px-1.5 py-0 flex items-center gap-1 border-primary/40 text-primary font-medium">
                  <UserRound className="h-2.5 w-2.5" /> {job.supervisor.name}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono-plex text-[11px] font-semibold text-primary">#{job.job_number}</span>
              <span className="text-sm font-medium truncate">{job.customer_name}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 min-w-0">
              <AddressLink address={job.address} city={job.city}>{job.address}</AddressLink>
              <span className="mx-0.5">·</span>
              <Calendar className="h-3 w-3 shrink-0" />
              {job.scheduled_time}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {!job.removed_from_source && <Badge className={`${statusColor[job.status]} text-[9px]`}>{t(job.status)}</Badge>}
            <RemovedBadge job={job} t={t} small />
            <JobTypeBadge type={job.job_type} t={t} small />
            <DayProgressBadge job={job} t={t} small />
            {job.open_issues > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-destructive font-medium">
                <AlertTriangle className="h-3 w-3" /> {job.open_issues}
              </span>
            )}
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card
      data-testid={`job-card-${job.job_number}`}
      onClick={onOpen}
      className="p-4 cursor-pointer hover:bg-muted/40 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {job.crew?.code && (
              <Badge data-testid="job-card-crew-badge" className="bg-primary text-primary-foreground font-mono-plex text-[28px] leading-none tracking-wider px-4 py-2 rounded-lg">
                {job.crew.code}
              </Badge>
            )}
            {job.supervisor?.name && (
              <Badge variant="outline" data-testid="job-card-supervisor-badge" className="text-[13px] flex items-center gap-1.5 px-2.5 py-1 border-primary/40 text-primary font-semibold">
                <UserRound className="h-3.5 w-3.5" /> {job.supervisor.name}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-plex text-xs font-semibold text-primary">#{job.job_number}</span>
            <span className="font-medium truncate">{job.customer_name}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1.5 min-w-0">
            <AddressLink address={job.address} city={job.city} iconClass="h-3.5 w-3.5" />
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(job.scheduled_date).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { weekday: "short", month: "short", day: "numeric" })} · {job.scheduled_time}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {!job.removed_from_source && <Badge className={`${statusColor[job.status]} text-[10px]`}>{t(job.status)}</Badge>}
          <RemovedBadge job={job} t={t} />
          <JobTypeBadge type={job.job_type} t={t} />
          <DayProgressBadge job={job} t={t} />
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t">
        <div className="text-xs text-muted-foreground">
          {job.description}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> {job.photo_count}</span>
          {job.open_issues > 0 && (
            <span className="flex items-center gap-1 text-destructive font-medium">
              <AlertTriangle className="h-3.5 w-3.5" /> {job.open_issues}
            </span>
          )}
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

export default function JobsPage() {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [crewFilter, setCrewFilter] = useState([]); // [] = all teams
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const isSupervisor = user.role === "supervisor";
  // Collapsible "other supervisors" section — remembered between visits
  const [othersOpen, setOthersOpen] = useState(() => localStorage.getItem("pmf_others_jobs_open") !== "0");
  const toggleOthers = () => {
    setOthersOpen((prev) => {
      localStorage.setItem("pmf_others_jobs_open", prev ? "0" : "1");
      return !prev;
    });
  };

  const load = useCallback(async () => {
    // Supervisors see every crew's jobs, split into "mine" vs "other supervisors"
    const cacheKey = `osc_jobs_cache_${user.id}`;
    try {
      const res = await api.get(`/jobs?user_id=${user.id}${isSupervisor ? "&scope=all" : ""}`);
      setJobs(res.data);
      try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), jobs: res.data })); } catch (e) { /* quota */ }
    } catch (e) {
      // OFFLINE: show the last synced schedule (kept locally on every successful load)
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
        if (cached?.jobs) setJobs(cached.jobs);
      } catch (e2) { /* ignore */ }
    }
    setLoading(false);
  }, [user.id, isSupervisor]);

  useEffect(() => { load(); }, [load]);

  // Jobs removed from the PMHR matrix will NOT happen: never listed under Active/Upcoming (any role) — only in Past/All
  let filtered = statusFilter === "all" ? jobs : jobs.filter((j) => j.time_bucket === statusFilter && !((statusFilter === "active" || statusFilter === "upcoming") && j.removed_from_source));
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter((j) => j.job_number.includes(q) || j.customer_name.toLowerCase().includes(q) || j.address.toLowerCase().includes(q));
  }

  // Extra filters (teams + date range) — available on Past / Upcoming / All
  const showFilters = ["past", "future", "all"].includes(statusFilter);
  const naturalCrew = (code) => {
    const m = (code || "").toUpperCase().match(/^([A-Z]+)\s*(\d+)$/);
    return m ? [m[1], parseInt(m[2], 10)] : [code || "", Number.MAX_SAFE_INTEGER];
  };
  const crewCodes = [...new Set(jobs.map((j) => j.crew?.code).filter(Boolean))].sort((a, b) => {
    const [pa, na] = naturalCrew(a), [pb, nb] = naturalCrew(b);
    return pa !== pb ? pa.localeCompare(pb) : na - nb;
  });
  if (showFilters) {
    if (crewFilter.length) filtered = filtered.filter((j) => crewFilter.includes(j.crew?.code));
    if (dateFrom) filtered = filtered.filter((j) => (j.scheduled_date || "").slice(0, 10) >= dateFrom);
    if (dateTo) filtered = filtered.filter((j) => (j.scheduled_date || "").slice(0, 10) <= dateTo);
  }
  const hasActiveFilters = crewFilter.length > 0 || dateFrom || dateTo;
  const toggleCrew = (code) => {
    setCrewFilter((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };
  const clearFilters = () => { setCrewFilter([]); setDateFrom(""); setDateTo(""); };

  // Quick date presets (week = Sunday..Saturday, matching US jobsite convention)
  const datePresets = (() => {
    const now = new Date();
    const ws = new Date(now); ws.setDate(now.getDate() - now.getDay());
    const we = new Date(ws); we.setDate(ws.getDate() + 6);
    const ms = new Date(now.getFullYear(), now.getMonth(), 1);
    const me = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const lws = new Date(ws); lws.setDate(ws.getDate() - 7);
    const lwe = new Date(ws); lwe.setDate(ws.getDate() - 1);
    const lms = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lme = new Date(now.getFullYear(), now.getMonth(), 0);
    const nws = new Date(we); nws.setDate(we.getDate() + 1);
    const nwe = new Date(nws); nwe.setDate(nws.getDate() + 6);
    const nms = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nme = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    const list = [
      { key: "this_week", label: t("preset_this_week"), from: fmtD(ws), to: fmtD(we) },
      { key: "this_month", label: t("preset_this_month"), from: fmtD(ms), to: fmtD(me) },
    ];
    if (statusFilter === "past" || statusFilter === "all") {
      list.push({ key: "last_week", label: t("preset_last_week"), from: fmtD(lws), to: fmtD(lwe) });
      list.push({ key: "last_month", label: t("preset_last_month"), from: fmtD(lms), to: fmtD(lme) });
    }
    if (statusFilter === "future" || statusFilter === "all") {
      const n7e = new Date(now); n7e.setDate(now.getDate() + 6);
      list.unshift({ key: "next_7_days", label: t("preset_next_7_days"), from: fmtD(now), to: fmtD(n7e) });
      list.push({ key: "next_week", label: t("preset_next_week"), from: fmtD(nws), to: fmtD(nwe) });
      list.push({ key: "next_month", label: t("preset_next_month"), from: fmtD(nms), to: fmtD(nme) });
    }
    return list;
  })();
  const applyPreset = (p) => {
    if (dateFrom === p.from && dateTo === p.to) { setDateFrom(""); setDateTo(""); return; } // toggle off
    setDateFrom(p.from);
    setDateTo(p.to);
  };

  const applyThisWeek = () => {
    const r = thisWeekRange();
    setDateFrom(r.from);
    setDateTo(r.to);
  };

  const next7Range = () => {
    const now = new Date();
    const end = new Date(now); end.setDate(now.getDate() + 6);
    return { from: fmtD(now), to: fmtD(end) };
  };
  const applyNext7 = () => {
    const r = next7Range();
    setDateFrom(r.from);
    setDateTo(r.to);
  };

  // "Past" ALWAYS opens with "This week"; "Future" ALWAYS opens with "Next 7 days".
  // Leaving the tab: clear the auto-applied range (only if the user didn't customize it).
  useEffect(() => {
    const w = thisWeekRange();
    const n7 = next7Range();
    if (statusFilter === "past") {
      applyThisWeek();
    } else if (statusFilter === "future") {
      applyNext7();
    } else if ((dateFrom === w.from && dateTo === w.to) || (dateFrom === n7.from && dateTo === n7.to)) {
      setDateFrom("");
      setDateTo("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Ascending crew order (PM1, PM2, ... PM10)
  const crewKey = (j) => {
    const code = (j.crew?.code || "ZZZ").toUpperCase();
    const m = code.match(/^([A-Z]+)\s*(\d+)$/);
    return m ? { prefix: m[1], num: parseInt(m[2], 10) } : { prefix: code, num: Number.MAX_SAFE_INTEGER };
  };
  const byCrew = (a, b) => {
    const ka = crewKey(a), kb = crewKey(b);
    if (ka.prefix !== kb.prefix) return ka.prefix.localeCompare(kb.prefix);
    return ka.num - kb.num;
  };
  // PMHR order: day, then start time (08:00 before 13:00)
  const byDateAsc = (a, b) => (new Date(a.scheduled_date) - new Date(b.scheduled_date))
    || (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
  const byDayDesc = (a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date);
  filtered = [...filtered].sort((a, b) => {
    if (statusFilter === "past") return byDayDesc(a, b) || byDateAsc(a, b) || byCrew(a, b);   // most recent day first, PMHR order inside
    if (statusFilter === "future") return byDateAsc(a, b) || byCrew(a, b);                   // nearest first
    return byCrew(a, b) || byDateAsc(a, b);                                                   // active/all: PM1, PM2, ... then time
  });

  const myJobs = isSupervisor ? filtered.filter((j) => j.supervisor_id === user.id) : filtered;
  const otherJobs = isSupervisor ? filtered.filter((j) => j.supervisor_id !== user.id) : [];
  // Past/Upcoming: group by day with ———— date ———— separators (PM order inside each day)
  const grouped = statusFilter === "past" || statusFilter === "future";

  return (
    <div className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-6">
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">
        {user.role === "installer" ? t("my_jobs") : t("all_jobs")}
      </h1>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="jobs-search-input"
            placeholder={t("search_jobs")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="jobs-filter-all">{t("all")}</TabsTrigger>
            <TabsTrigger value="active" data-testid="jobs-filter-active">{t("tab_active")}</TabsTrigger>
            <TabsTrigger value="past" data-testid="jobs-filter-past" onClick={() => { if (statusFilter === "past") applyThisWeek(); }}>{t("tab_past")}</TabsTrigger>
            <TabsTrigger value="future" data-testid="jobs-filter-future" onClick={() => { if (statusFilter === "future") applyNext7(); }}>{t("tab_future")}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {showFilters && (
        <Card className="p-3 mb-5 space-y-2.5" data-testid="jobs-extra-filters">
          {crewCodes.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mr-1">
                <Filter className="h-3.5 w-3.5" /> {t("filter_teams")}
              </span>
              <button
                type="button"
                data-testid="jobs-crew-filter-all"
                onClick={() => setCrewFilter([])}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${crewFilter.length === 0 ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50"}`}
              >
                {t("all")}
              </button>
              {crewCodes.map((code) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`jobs-crew-filter-${code}`}
                  onClick={() => toggleCrew(code)}
                  className={`text-xs font-mono-plex font-semibold px-2.5 py-1 rounded-full border transition-colors ${crewFilter.includes(code) ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50"}`}
                >
                  {code}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mr-1">
              <Calendar className="h-3.5 w-3.5" /> {t("filter_dates")}
            </span>
            {datePresets.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`jobs-date-preset-${p.key}`}
                onClick={() => applyPreset(p)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${dateFrom === p.from && dateTo === p.to ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50"}`}
              >
                {p.label}
              </button>
            ))}
            <Input
              data-testid="jobs-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-[150px] text-xs"
            />
            <span className="text-xs text-muted-foreground">—</span>
            <Input
              data-testid="jobs-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-[150px] text-xs"
            />
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground" data-testid="jobs-clear-filters" onClick={clearFilters}>
                <X className="h-3.5 w-3.5 mr-1" /> {t("clear_filters")}
              </Button>
            )}
            <Badge variant="secondary" className="text-[10px] ml-auto" data-testid="jobs-filter-count">{filtered.length}</Badge>
          </div>
        </Card>
      )}

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => <Card key={i} className="h-32 animate-pulse bg-muted" />)}
        </div>
      )}

      {!loading && isSupervisor ? (
        <>
          {/* My jobs */}
          <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2" data-testid="jobs-section-mine">
            <Briefcase className="h-4 w-4 text-primary" /> {t("my_jobs")}
            <Badge variant="secondary" className="text-[10px]">{myJobs.length}</Badge>
          </h2>
          <div className="mb-8">
            <GroupedJobs
              jobs={myJobs}
              grouped={grouped}
              gridClass="grid grid-cols-1 md:grid-cols-2 gap-3"
              lang={lang}
              t={t}
              renderCard={(job) => (
                <JobCard key={job.id} job={job} lang={lang} t={t} showSupervisor={false} onOpen={() => navigate(`/jobs/${job.id}`)} />
              )}
            />
          </div>
          {myJobs.length === 0 && <div className="text-center text-sm text-muted-foreground py-8 mb-4">—</div>}

          {/* Other supervisors' jobs — compact, visually secondary, collapsible */}
          <button
            type="button"
            data-testid="jobs-section-others"
            onClick={toggleOthers}
            className="w-full font-display font-semibold text-base text-muted-foreground mb-3 flex items-center gap-2 rounded-lg py-1 hover:text-foreground transition-colors"
          >
            <UserRound className="h-4 w-4" /> {t("other_supervisors_jobs")}
            <Badge variant="secondary" className="text-[10px]">{otherJobs.length}</Badge>
            <ChevronDown className={`h-4 w-4 ml-auto transition-transform duration-200 ${othersOpen ? "" : "-rotate-90"}`} data-testid="jobs-others-chevron" />
          </button>
          {othersOpen && (
            <>
              <GroupedJobs
                jobs={otherJobs}
                grouped={grouped}
                gridClass="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5"
                lang={lang}
                t={t}
                renderCard={(job) => (
                  <JobCard key={job.id} job={job} lang={lang} t={t} showSupervisor compact onOpen={() => navigate(`/jobs/${job.id}`)} />
                )}
              />
              {otherJobs.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">—</div>}
            </>
          )}
        </>
      ) : !loading && (
        <>
          <GroupedJobs
            jobs={filtered}
            grouped={grouped}
            gridClass="grid grid-cols-1 md:grid-cols-2 gap-3"
            lang={lang}
            t={t}
            renderCard={(job) => (
              <JobCard key={job.id} job={job} lang={lang} t={t} showSupervisor={false} onOpen={() => navigate(`/jobs/${job.id}`)} />
            )}
          />
          {filtered.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-16">—</div>
          )}
        </>
      )}
    </div>
  );
}
